import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { KeyRound, Lock, CheckCircle2, XCircle, Activity, MapPin, RefreshCw } from "lucide-react";

function parseUA(ua) {
  if (!ua) return "Unknown device";
  const s = ua;
  let os = "Unknown OS";
  if (/iPhone|iPad|iPod/.test(s)) os = "iOS";
  else if (/Android/.test(s)) os = "Android";
  else if (/Mac OS X|Macintosh/.test(s)) os = "Mac";
  else if (/Windows/.test(s)) os = "Windows";
  else if (/Linux/.test(s)) os = "Linux";
  let browser = "Browser";
  if (/Edg\//.test(s)) browser = "Edge";
  else if (/OPR\//.test(s)) browser = "Opera";
  else if (/Chrome\//.test(s)) browser = "Chrome";
  else if (/Firefox\//.test(s)) browser = "Firefox";
  else if (/Safari\//.test(s)) browser = "Safari";
  return `${browser} on ${os}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 7) return `${dd}d ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function reasonLabel(reason) {
  switch (reason) {
    case "unknown_email": return "Unknown email";
    case "wrong_password": return "Wrong password";
    case "unknown_device": return "Unknown device";
    case "locked": return "PIN was locked";
    case "wrong_pin": return "Wrong PIN";
    case "wrong_pin_locked": return "Wrong PIN — locked";
    default: return "Failed";
  }
}

export function RecentLoginsCard() {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/auth/recent-logins", { params: { limit: 15 } })
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <section data-testid="recent-logins-card" className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-[#F7E7DE] flex items-center justify-center flex-shrink-0">
            <Activity className="h-5 w-5 text-[#9A462B]" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold text-slate-900">Recent Logins</h3>
            <p className="text-xs text-slate-500 mt-0.5">Spot any sign-in you don't recognise</p>
          </div>
        </div>
        <button data-testid="recent-logins-refresh" onClick={load}
          className="p-2 -mr-1 text-slate-400 active:text-[#9A462B]"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
      </div>

      {events === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-slate-400">No login activity yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="recent-logins-list">
          {events.map((e, i) => (
            <li key={i} data-testid={`recent-login-row-${i}`} className="py-3 flex items-start gap-3">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${e.success ? "bg-emerald-50" : "bg-red-50"}`}>
                {e.success ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-slate-800 flex items-center gap-1">
                    {e.method === "pin" ? <KeyRound className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                    {e.method === "pin" ? "PIN" : "Password"}
                  </span>
                  {e.success ? (
                    <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Success</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase text-red-700 bg-red-50 rounded-full px-2 py-0.5">{reasonLabel(e.reason)}</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{parseUA(e.user_agent)}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                  <span className="tnum">{timeAgo(e.created_at)}</span>
                  {e.ip && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{e.ip}</span>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default RecentLoginsCard;
